"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import * as React from "react";
import { BrandLogoDark, BrandLogoIconDark } from "@/components/icons";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useToast } from "@/components/ui/use-toast";

export function Logo({ href }: { href?: string }) {
  const router = useRouter();
  const { toast } = useToast();

  const logoContainerRef = React.useRef<HTMLDivElement | null>(null);

  const resolvedHref = React.useMemo(() => {
    return href ?? "/brand";
  }, [href]);

  const handleBackSelect = React.useCallback(
    (_event: Event) => {
      router.back();
    },
    [router],
  );

  const handleForwardSelect = React.useCallback(
    (_event: Event) => {
      try {
        window.history.forward();
      } catch {
        toast({
          description: "Forward navigation is not available.",
          title: "Error",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  const copyTextToClipboard = React.useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fallback for environments without clipboard permissions.
      try {
        const input = document.createElement("input");
        input.value = text;
        input.setAttribute("readonly", "true");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(input);
        return ok;
      } catch {
        return false;
      }
    }
  }, []);

  const copySvgElementAsText = React.useCallback(
    async (svg: SVGSVGElement | null, label: string) => {
      if (!svg) {
        toast({
          description: `Could not find ${label} SVG in the DOM.`,
          title: "Error",
          variant: "destructive",
        });
        return;
      }

      const svgText = new XMLSerializer().serializeToString(svg);
      const ok = await copyTextToClipboard(svgText);

      toast(
        ok
          ? { description: `${label} copied as SVG.`, title: "Copied" }
          : {
              description: `Failed to copy ${label} as SVG.`,
              title: "Error",
              variant: "destructive",
            },
      );
    },
    [copyTextToClipboard, toast],
  );

  const handleCopyLogoAsSvgSelect = React.useCallback(
    (_event: Event) => {
      const svg =
        logoContainerRef.current?.querySelector<SVGSVGElement>('svg[viewBox^="0 0 81"]') ?? null;
      void copySvgElementAsText(svg, "Logo");
    },
    [copySvgElementAsText],
  );

  const handleCopyWordmarkAsSvgSelect = React.useCallback(
    (_event: Event) => {
      const svg =
        logoContainerRef.current?.querySelector<SVGSVGElement>('svg[viewBox^="0 0 411"]') ?? null;
      void copySvgElementAsText(svg, "Wordmark");
    },
    [copySvgElementAsText],
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger className="flex h-fit w-fit items-center justify-center">
        <div ref={logoContainerRef}>
          <Link aria-label="Linguolink home" href={resolvedHref} title="Logo">
            <div className="flex items-center justify-center">
              <div className="block sm:hidden">
                <BrandLogoIconDark
                  aria-label="Linguolink home"
                  className="fill-foreground stroke-foreground text-foreground"
                  height={40}
                  role="img"
                  width={40}
                />
              </div>
              <div className="hidden sm:block">
                {/* Wordmark SVG: viewBox is 411x87, so keep the aspect ratio. */}
                <BrandLogoDark
                  aria-label="Linguolink home"
                  className="fill-foreground stroke-foreground text-foreground"
                  height={21}
                  role="img"
                  width={100}
                />
              </div>
            </div>
          </Link>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-64">
        <ContextMenuItem inset onSelect={handleBackSelect}>
          Back
          <ContextMenuShortcut>⌘[</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuItem inset onSelect={handleForwardSelect}>
          Forward
          <ContextMenuShortcut>⌘]</ContextMenuShortcut>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={handleCopyLogoAsSvgSelect}>Copy logo as SVG</ContextMenuItem>
        <ContextMenuItem onSelect={handleCopyWordmarkAsSvgSelect}>
          Copy wordmark as SVG
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem>
          <Link href="/brand">Visit brand guidelines</Link>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
